import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { FAMILY_DASHBOARD_ENABLED } from "./config/features";
import { AuthProvider } from "./contexts/AuthContext";
import { AdminProvider } from "./contexts/AdminContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { PwaInstallProvider } from "./contexts/PwaInstallContext";
import { PremiumFeaturesProvider } from "./components/premium/PremiumFeatures";
import { LoadingState } from "./components/ui/states";
import SessionExpiredNotice from "./components/shared/SessionExpiredNotice";
import AuthenticatedHubGuard from "./components/shared/AuthenticatedHubGuard";
import PwaInstallPrompt from "./components/shared/PwaInstallPrompt";
import RobotsMeta from "./components/shared/RobotsMeta";

// Tier 1 emergency routes — eager load for fast QR scan path
import LandingPage from "./pages/LandingPage";
import ReportEmergencyPage from "./pages/ReportEmergencyPage";
import GuidedRescuePage from "./pages/GuidedRescuePage";
import PublicEmergencyInfoPage from "./pages/PublicEmergencyInfoPage";
import NearbyServicesPage from "./pages/NearbyServicesPage";
import BystanderReportLookupPage from "./pages/BystanderReportLookupPage";

// Marketing, admin, owner flows — lazy loaded
const ProfessionalHomePage = lazy(() => import("./pages/ProfessionalHomePage"));
const FamilyDashboardPage = lazy(() => import("./pages/FamilyDashboardPage"));
const SetupPage = lazy(() => import("./pages/SetupPage"));
const EmergencyInfoFormPage = lazy(() => import("./pages/EmergencyInfoFormPage"));
const OwnerLoginPage = lazy(() => import("./pages/OwnerLoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const MyOrdersPage = lazy(() => import("./pages/MyOrdersPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const OrderTrackingPage = lazy(() => import("./pages/OrderTrackingPage"));
const OrderPage = lazy(() => import("./pages/OrderPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const DocumentVaultPage = lazy(() => import("./pages/DocumentVaultPage"));
const BloodSosPage = lazy(() => import("./pages/BloodSosPage"));
const ExportCardPage = lazy(() => import("./pages/ExportCardPage"));

const LegalPage = lazy(() => import("./pages/LegalPage"));

const PageLoader = () => <LoadingState label="Loading..." />;

function App() {
  return (
    <LanguageProvider>
      <PwaInstallProvider>
      <PremiumFeaturesProvider>
      <AuthProvider>
        <AdminProvider>
          <Router>
            <AuthenticatedHubGuard />
            <SessionExpiredNotice />
            <PwaInstallPrompt />
            <RobotsMeta />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<ProfessionalHomePage />} />

                <Route
                  path="/family"
                  element={FAMILY_DASHBOARD_ENABLED ? <FamilyDashboardPage /> : <Navigate to="/profile" replace />}
                />
                <Route path="/guide" element={<GuidePage />} />
                <Route path="/privacy" element={<LegalPage page="privacy" />} />
                <Route path="/terms" element={<LegalPage page="terms" />} />
                <Route path="/refund-policy" element={<LegalPage page="refund" />} />
                <Route path="/shipping-policy" element={<LegalPage page="shipping" />} />
                <Route path="/contact" element={<LegalPage page="contact" />} />
                <Route path="/login" element={<Navigate to="/?login=1" replace />} />
                <Route path="/order/:orderId/track" element={<OrderTrackingPage />} />
                <Route path="/order" element={<OrderPage />} />
                <Route path="/orders" element={<MyOrdersPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/nearby-services" element={<NearbyServicesPage />} />

                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />

                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />

                <Route path="/:qrId" element={<LandingPage />} />
                <Route path="/:qrId/setup" element={<SetupPage />} />
                <Route path="/:qrId/info" element={<EmergencyInfoFormPage />} />
                <Route path="/:qrId/public" element={<PublicEmergencyInfoPage />} />
                <Route path="/:qrId/edit" element={<OwnerLoginPage />} />
                <Route path="/:qrId/report" element={<ReportEmergencyPage />} />
                <Route path="/:qrId/report-lookup" element={<BystanderReportLookupPage />} />
                <Route path="/:qrId/rescue" element={<GuidedRescuePage />} />
                <Route path="/:qrId/nearby" element={<NearbyServicesPage />} />
                <Route path="/:qrId/documents" element={<DocumentVaultPage />} />
                <Route path="/:qrId/blood-sos" element={<BloodSosPage />} />
                <Route path="/:qrId/export-card" element={<ExportCardPage />} />
                <Route path="/:qrId/forgot-password" element={<ForgotPasswordPage />} />

                <Route
                  path="*"
                  element={
                    <div className="min-h-screen bg-background flex items-center justify-center p-4">
                      <div className="bg-card rounded-lg border border-border p-8 text-center max-w-sm shadow-card">
                        <h1 className="text-card-title font-bold text-foreground mb-2">Page not found</h1>
                        <p className="text-body text-muted-foreground mb-6">This page does not exist.</p>
                        <a
                          href="/"
                          className="inline-flex items-center gap-2 bg-primary text-inverse font-semibold text-body px-6 py-3 rounded-md hover:bg-primary-hover active:bg-primary-active shadow-btn min-h-12"
                        >
                          Go home
                        </a>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </AdminProvider>
      </AuthProvider>
      </PremiumFeaturesProvider>
      </PwaInstallProvider>
    </LanguageProvider>
  );
}

export default App;
